/**
 * Permission types for the sharing system.
 * These match the backend API at /api/v1/permissions
 */
import { RELATION_VALUES, type RelationType } from '../../../generated/config-enums';

// Relation enum is single-sourced from shared/config/permissions.config.json
// (P-9 / D16) — re-exported here so existing `from './types'` imports keep
// working. Never hand-write the value list, ordering, or colour/label maps.
export type { RelationType };
export type SubjectType = 'user' | 'group' | 'company' | 'role';

export interface Subject {
  type: SubjectType;
  id: string;
  name?: string;
  email?: string;
  avatar_url?: string | null;
  /** True/false for a user subject, null/undefined otherwise. Drives the admin
   *  ownership-recovery affordance in ShareDialog (design §4.1). */
  is_active?: boolean | null;
}

export interface Resource {
  type: string;
  id: string;
}

// The two sharing request bodies ARE the generated models — `usePermissions`
// already posts through the orval fetchers (`grantAccessApiV1PermissionsGrantPost`
// / `revokeAccessApiV1PermissionsRevokeDelete`), whose body params are these, so
// the copies below had no reader and could only drift. Re-exported (not aliased)
// because the name matches the generated one exactly.
//
// `GrantAccessRequest.subject`/`resource` are `PermissionSubjectRef`/
// `PermissionResourceRef` — `{ type: string; id: string }`, the wire pair — NOT the
// display-bearing `Subject` above. `expires_at` is `string | null` ("null = never
// expires") and `RevokeAccessRequest.relation` is `RelationType | null` ("null
// revokes all relations"); both hooks already send an explicit `?? null`.
export type { GrantAccessRequest } from '@/generated/api/models/grantAccessRequest';
export type { RevokeAccessRequest } from '@/generated/api/models/revokeAccessRequest';

export interface AccessEntry {
  subject: {
    type: SubjectType;
    id: string;
    name?: string;
    email?: string;
    avatar_url?: string | null;
    is_active?: boolean | null;
  };
  relation: RelationType;
  source: 'direct' | 'rule' | 'inherited';
  source_detail?: string;
  granted_at?: string;
  expires_at?: string;
}

export interface ParentEntity {
  type: string;
  id: string;
  name?: string;
}

export interface AccessListResponse {
  access: AccessEntry[];
  inherited_access?: AccessEntry[];
  parent_entity?: ParentEntity;
}

// `/permissions/check`'s response, from the generated model. `relation`, `source`
// and `source_detail` are each `X | null` there (the copy below spelled them
// `X | undefined`); `source` is a bare `string | null`, deliberately — the backend
// widens it so a new provenance kind ("rule", an inherited-from-parent variant)
// degrades instead of failing response validation. `useCheckPermission` already
// returns this generated type, so the copy had no reader.
export type { PermissionCheckResponse } from '@/generated/api/models/permissionCheckResponse';

/**
 * Hierarchy of relations - higher index = more permissions. Single-sourced from
 * the config enum (viewer < commenter < editor < owner); do not re-order by hand.
 */
export const RELATION_HIERARCHY: readonly RelationType[] = RELATION_VALUES;
